import { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { BodyText, InitialsAvatar, TabButton, TitleText } from 'Application/components';
import MainCard from 'ui-component/cards/MainCard';
import { TravelInsuranceTab } from 'Application/models';
import { AssignedAgenciesData } from 'Application/data';
import AddNewPlan from 'Application/components/Modals/CreatePlan';
import AddBenefit from 'Application/components/Modals/AddBenefit';
import { useTripStore, useBenefitsStore, useCreatePlanStore, useDestinationStore, useAgencyStore } from 'Application/store';
import AddDestination from 'Application/components/Modals/AddDestination';
import Loader from 'ui-component/Loader';
import { ExchangeRates } from './TabSections/ExchangeRates';
import { Plans } from './TabSections/Plans';
import { Agencies } from './TabSections/Agencies';
import { Benefits } from './TabSections/Benefits';
import { Destinations } from './TabSections/Destinations';
import AddExchangeRate from 'Application/components/Modals/AddExchangeRate';
import { getUserRegionFromStorage } from 'Application/utils/AppSessionStorage';

interface TravelSetupProps {}

export const TravelSetup: FC<TravelSetupProps> = () => {
    const [activeTab, setActiveTab] = useState<TravelInsuranceTab>(TravelInsuranceTab.Plans);
    const [isAddPlanOpen, setIsAddPlanDialogOpen] = useState(false);
    const [isAddBenefitOpen, setIsAddBenefitDialogOpen] = useState(false);
    const [isAddDestinationOpen, setIsAddDestinationDialogOpen] = useState(false);
    const [isAddExchangeRatesOpen, setIsAddExchangeRatesOpen] = useState(false);
    const [isExchangeRateAdded, setIsExchangeRateAdded] = useState(false);

    const { getPlan, isLoading, isError } = useCreatePlanStore();
    const { getDestinations } = useDestinationStore();
    const { getAgencies } = useAgencyStore();
    const { getTrip, tripData } = useTripStore();
    const { getBenefits } = useBenefitsStore();

    const toggleSelectedItem = (value: TravelInsuranceTab) => {
        setActiveTab(value);
    };

    const addPlanDialog = () => {
        setIsAddPlanDialogOpen(true);
    };

    const addBenefitDialog = () => {
        setIsAddBenefitDialogOpen(true);
    };

    const addDestinationDialog = () => {
        setIsAddDestinationDialogOpen(true);
    };

    const addExchangeRateDialog = () => {
        setIsAddExchangeRatesOpen(true);
    };

    useEffect(() => {
        getPlan();
        getTrip();
        getBenefits();
        getDestinations();

        //    const region = getUserRegionFromStorage();

        //            if (region) {
        //                getAgencies(region);
        //            }
    }, []);

    if (isLoading) {
        return <Loader />;
    }

    if (isError) {
        return <Box>Error loading plans. Please try again later.</Box>;
    }

    return (
        <>
            <MainCard>
                <Box className="flex items-end space-x-6 mb-6">
                    <InitialsAvatar initials={'TI'} />
                    <Box>
                        <TitleText text={'Travel Insurance'} />
                        <BodyText text={'Travel with confidence—comprehensive coverage for every journey!'} />
                    </Box>
                </Box>
                <Box className="flex flex-wrap items-center space-x-2 mb-6">
                    <TabButton
                        isActive={activeTab === TravelInsuranceTab.Plans}
                        text={'Plans'}
                        onClick={() => toggleSelectedItem(TravelInsuranceTab.Plans)}
                    />
                    <TabButton
                        isActive={activeTab === TravelInsuranceTab.Agencies}
                        text={'Agencies'}
                        onClick={() => toggleSelectedItem(TravelInsuranceTab.Agencies)}
                    />
                    <TabButton
                        isActive={activeTab === TravelInsuranceTab.Benefits}
                        text={'Benefits'}
                        onClick={() => toggleSelectedItem(TravelInsuranceTab.Benefits)}
                    />
                    <TabButton
                        isActive={activeTab === TravelInsuranceTab.Destinations}
                        text={'Destinations'}
                        onClick={() => toggleSelectedItem(TravelInsuranceTab.Destinations)}
                    />
                    <TabButton
                        isActive={activeTab === TravelInsuranceTab.ExchangeRates}
                        text={'Exchange Rates'}
                        onClick={() => toggleSelectedItem(TravelInsuranceTab.ExchangeRates)}
                    />
                </Box>
                {activeTab === TravelInsuranceTab.Plans && <Plans addPlanDialog={addPlanDialog} />}
                {activeTab === TravelInsuranceTab.Agencies && <Agencies />}
                {activeTab === TravelInsuranceTab.Benefits && <Benefits addBenefitDialog={addBenefitDialog} />}
                {activeTab === TravelInsuranceTab.Destinations && <Destinations addDestinationDialog={addDestinationDialog} />}
                {activeTab === TravelInsuranceTab.ExchangeRates && (
                    <ExchangeRates
                        addExchangeRateDialog={addExchangeRateDialog}
                        isExchangeRateAdded={isExchangeRateAdded}
                        setIsAddedRate={setIsExchangeRateAdded}
                    />
                )}
            </MainCard>
            <AddNewPlan isOpen={isAddPlanOpen} setIsOpen={setIsAddPlanDialogOpen} />
            <AddBenefit isOpen={isAddBenefitOpen} setIsOpen={setIsAddBenefitDialogOpen} />
            <AddDestination isOpen={isAddDestinationOpen} setIsOpen={setIsAddDestinationDialogOpen} />
            <AddExchangeRate
                isOpen={isAddExchangeRatesOpen}
                setIsOpen={setIsAddExchangeRatesOpen}
                setIsAddedRate={setIsExchangeRateAdded}
            />
        </>
    );
};
